import React, { useState, useEffect, forwardRef } from 'react';
import { PresentationScript } from '@components';
import { SlideHeader, Disclaimer, Video } from './shared';
import { SlideDataProvider } from '@context/slide-data';
import { renderCurrentSlide } from '@utils/currentSlide';
import { Slide } from './styles';
import { SlideBody } from './shared';
import { usePresentation } from '@context/presentation';
import { loadDimensionsAndViewport, useRefDimensions } from '@hooks/useRefDimensions';
import { getErrorMessage } from '@utils/errorHandling';
import { retrieveSavedSteps } from '@utils/browserStorage';
import { useCurrentSlideQuery } from '@queries/slides';
import { Profile, SLIDE_IDS, SHARED_STYLES } from '@constants';

const { CUSTOMER_SERVICE } = Profile;
const { termOptions, summary } = SLIDE_IDS;
    
export const CurrentSlide = forwardRef(function CurrentSlide(props, ref) {
    const { clientViewing, client, profile } = usePresentation();
    const { leadId, firmShortName } = client;
    const { slideState, dispatch, children, pdfStyleActive, showScript, setShowScript, setShowViewportButton } = props;
    const { viewport, stepsReviewed } = slideState;
    const slideId = props.slideId ?? slideState.slideId;
    const [initialTermLoaded, setInitialTermLoaded] = useState({
        [termOptions]: false,
        [summary]: false
    });

    const currentTermData =  { 
        initialTermLoaded, 
        setInitialTermLoaded, 
        slideState, 
        dispatch 
    };

    const { isLoading, error, data } = useCurrentSlideQuery(slideId, leadId, firmShortName, currentTermData);
   
    // Assigns a resize observer to notify rep of updated dimensions on resize (slide only, not PDF yet)
    if (clientViewing) {
        useRefDimensions(dispatch, ref, leadId);
    }
    
    useEffect(() => {
        const { slideDimensions } = loadDimensionsAndViewport(dispatch, ref);

        if (slideDimensions) {
            const { maxSlideWidth } = SHARED_STYLES;
            const isNonStandardViewport = slideDimensions.width < parseInt(maxSlideWidth);

            if (isNonStandardViewport && !clientViewing && setShowViewportButton) {
                setShowViewportButton(true);
            }    
        } 

        const slidesWithSteps = Object.keys(stepsReviewed);

        slidesWithSteps.forEach(slide => {
            retrieveSavedSteps(slide, dispatch);
        });
    }, [ref]);

    return (
        <SlideDataProvider
            slideState={slideState}
            dispatch={dispatch}
            data={data}
            sectionsVisible={showScript}>
                <Slide
                    viewport={viewport}
                    ref={ref}
                    pdfStyleActive={pdfStyleActive}
                    data-testid='current-slide'>
                    {
                        isLoading && <h3>Loading slide data...</h3>
                    }
                    {
                        error && <div>An error has occurred: { getErrorMessage(error) }</div>
                    }
                    {
                        data && (
                        <>
                            {children}
                            { data.videoUrl ? 
                                <Video /> :
                                <> 
                                    { data.name !== 'Introduction' && <SlideHeader /> }
                                        <SlideBody >   
                                            {renderCurrentSlide(data.name, clientViewing)}
                                        </SlideBody>
                                    { profile !== CUSTOMER_SERVICE && <Disclaimer name={data.name} />}
                                </>
                            } 
                        </>)
                    }
                </Slide>
                {
                    !clientViewing && <PresentationScript
                                        showScript={showScript}
                                        setShowScript={setShowScript}/>
                }
        </SlideDataProvider>
    );
});

export default CurrentSlide;
